import { getNormalEnum } from "@hex/common";
import { useCallback, useEffect, useState } from "react";
import { Literal, Static, Union } from "runtypes";

const StateLiteral = Union(
  Literal("LOADING"),
  Literal("ERROR"),
  Literal("READY"),
);

export type State = Static<typeof StateLiteral>;

export const State = getNormalEnum(StateLiteral);

const getScript = (src: string) =>
  document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);

const setStateOnScript = (script: HTMLScriptElement, state: State) => {
  script.setAttribute("data-state", state);
};

const getStateFromScript = (script: HTMLScriptElement | null): State => {
  const state = script?.getAttribute("data-state");
  if (state == null) {
    return State.LOADING;
  }
  if (StateLiteral.guard(state)) {
    return state;
  }
  return State.ERROR;
};

/**
 * Binds handlers for load, error, and custom "reload" event. Returns routine to
 * unbind handlers.
 */
const subscribe = ({
  onLoad: onLoad_,
  script,
  setScript,
  setState,
}: {
  script: HTMLScriptElement;
  setState: (state: State) => void;
  setScript: (script: HTMLScriptElement | null) => void;
  onLoad?: () => void;
}): (() => void) => {
  const onLoad = () => {
    if (getStateFromScript(script) !== State.READY) {
      onLoad_ && onLoad_();
    }
    setStateOnScript(script, State.READY);
    setState(State.READY);
  };
  const onError = () => {
    setStateOnScript(script, State.ERROR);
    setState(State.ERROR);
  };
  const onReload = () => {
    // We remove the script *we have a handle on* from the DOM - this will
    // succeed whether it's still in there or not.
    script.remove();
    // We then set the script to whatever is in the DOM *right now* - which is
    // either nothing (we're first to remove), or a script of the corresonding
    // source (we're in a line of reload handlers).
    setScript(getScript(script.src));
  };

  script.addEventListener("load", onLoad);
  script.addEventListener("error", onError);
  script.addEventListener("reload", onReload);

  return () => {
    script.removeEventListener("load", onLoad);
    script.removeEventListener("error", onError);
    script.removeEventListener("reload", onReload);
  };
};

interface Script {
  state: State;
  // Retry does nothing if you haven't already made an attempt to load the
  // script, or if it does not exist within the page.
  retry: () => void;
}

/**
 * @param id Identifier attached to script tag.
 * @param src
 * @param onLoad Optional routine to be invoked exactly once when script is
 *   loaded. If separate hooks pass in different values for this for the same
 *   load, only one will be called.
 */
export const useScript = ({
  id,
  onLoad,
  src,
}: {
  id?: string;
  src: string;
  onLoad?: () => void;
}): Script => {
  const [script, setScript] = useState(() => getScript(src));
  const [state, setState] = useState(() => getStateFromScript(script));

  const retry = useCallback(() => {
    script?.dispatchEvent(new Event("reload"));
  }, [script]);

  useEffect(() => {
    const scriptInDom = getScript(src);
    if (scriptInDom !== script) {
      // A script was injected or replaced before we ran our hook - update it.
      setScript(scriptInDom);

      // No cleanup necessary here. Either we had handlers and they've already
      // been torn down, or this was null and we're setting it, in which case
      // useEffect will run again and bind fresh handlers.
      return;
    }

    if (script != null) {
      // We have an existing script, so ensure state is correct.
      setState(getStateFromScript(script));

      // Attach handlers, return cleanup routines.
      return subscribe({ script, setState, setScript, onLoad });
    } else {
      // No script exists. Build it, attach handlers, inject it, set it, then
      // return cleanup routines.
      const newScript = document.createElement("script");
      newScript.src = src;
      newScript.async = true;
      if (id) {
        newScript.id = id;
      }

      const unsubscribe = subscribe({
        script: newScript,
        setState,
        setScript,
        onLoad,
      });
      document.body.appendChild(newScript);
      setScript(newScript);

      return unsubscribe;
    }
  }, [script, src, id, onLoad]);

  return { state, retry };
};
